import {
  readAttributionCookie,
  restoreFacebookClickCookie,
  shareFacebookBrowserCookie,
} from './attribution-cookie.ts';
import { browserTrafficExcluded, metaTestEventCode, trafficCheckedRecently, visitorTrafficExcluded } from './analytics-traffic.ts';

export const ANALYTICS_IDS = { ga: 'G-VD1LW816DJ', clarity: 'vogr94j8hk', meta: '886785587117318' };
type AnyFn = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: AnyFn;
    clarity?: AnyFn & { q?: unknown[] };
    fbq?: AnyFn & { queue?: unknown[]; callMethod?: AnyFn; loaded?: boolean; version?: string; push?: AnyFn };
    _fbq?: unknown;
  }
}
export type AnalyticsSurface = 'website' | 'dashboard' | 'help' | 'sales';
const origins: Record<AnalyticsSurface, string> = {
  website: 'https://callsyncapp.com',
  dashboard: 'https://dash.callsyncapp.com',
  help: 'https://help.callsyncapp.com',
  sales: 'https://sales.callsyncapp.com',
};
const sensitiveKeys =
  /^(?:code|token|token_hash|access_token|refresh_token|authorization_id|email|phone|pairing_code|credential|password)$/i;
export function isSensitiveUrl(href: string, allowPasswordPage = false): boolean {
  const url = new URL(href);
  let values = url.search + url.hash;
  try {
    values = decodeURIComponent(values);
  } catch {
    return true;
  }
  return (
    (!allowPasswordPage && url.pathname === '/reset-password') ||
    /\/(?:oauth\/consent)(?:\/|$)/.test(url.pathname) ||
    [...url.searchParams.keys()].some((key) => sensitiveKeys.test(key)) ||
    /(?:access_token|refresh_token|token_hash|authorization_id|email|password)=/i.test(values)
  );
}
export function analyticsLocation(href: string): string {
  const url = new URL(href);
  url.search = '';
  url.hash = '';
  url.pathname = url.pathname.replace(/\/(contacts|calls|recordings)\/[^/]+/g, '/$1/detail');
  return url.href;
}
const fields = new Set([
  'method',
  'step',
  'action',
  'reason',
  'source',
  // Support ticket topic from the chat widget (setup, recordings, billing, account, bug, other).
  'category',
  'member_type',
  'payment_provider',
  'product_type',
  'purchase_mode',
  'currency',
  'value',
  'transaction_id',
  'term_months',
  'users',
  'storage_gb',
  'credits',
  'has_integrations',
  'recording_skipped',
  'agent_skipped',
  'company_size',
]);
function safeParams(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) =>
        fields.has(key) &&
        ((typeof value === 'number' && Number.isFinite(value)) ||
          typeof value === 'boolean' ||
          (typeof value === 'string' && value.length <= 100 && !/[@?&#=]|https?:/i.test(value))),
    ),
  );
}
/** Browser IDs for these come from `once(key, …)`; the dashboard server sends the same IDs. */
const standardMeta: Record<string, string> = {
  sign_up: 'CompleteRegistration',
  // Keyed `first_call:<org id>`; the api worker sends the same ID from the database queue.
  first_call_synced: 'StartTrial',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
  contact_click: 'Contact',
};

/** Shared by all production surfaces. Test/preview traffic and auth secrets are excluded. */
export function createAnalytics({
  win,
  doc,
  surface,
  enabled = true,
  checkTraffic = true,
}: {
  win: Window;
  doc: Document;
  surface: AnalyticsSurface;
  enabled?: boolean;
  checkTraffic?: boolean;
}) {
  type TrafficState = 'checking' | 'enabled' | 'excluded' | 'unavailable' | 'disabled';
  const production = enabled && win.location.origin === origins[surface];
  const browserExcluded = production && browserTrafficExcluded(win, doc);
  // Meta/GA test mode also runs for internal traffic; its server events are Test Events only.
  const testMode = production && !!metaTestEventCode(win, doc);
  let trafficState: TrafficState = !production
    ? 'disabled'
    : testMode
      ? 'enabled'
      : browserExcluded
        ? 'excluded'
        : checkTraffic && !trafficCheckedRecently(win, doc)
          ? 'checking'
          : 'enabled';
  let trafficRequest: Promise<void> | null = null;
  let waiting: Array<() => void> = [];
  function status() {
    try {
      doc.documentElement?.setAttribute(
        'data-analytics-status',
        testMode && trafficState === 'enabled' ? 'test' : trafficState,
      );
    } catch {
      /* Optional diagnostic. */
    }
  }
  function trafficGate(action: () => void): boolean {
    if (trafficState === 'enabled') return false;
    if (trafficState !== 'checking') return true;
    if (waiting.length < 100) waiting.push(action);
    trafficRequest ??= visitorTrafficExcluded(win, doc)
      .then((excluded) => {
        trafficState = excluded ? 'excluded' : 'enabled';
        status();
        const actions = waiting;
        waiting = [];
        if (excluded) {
          pending = [];
          savePending();
          return;
        }
        for (const task of actions) task();
      })
      .catch(() => {
        // Fail closed for measurement only. Auth, the application and checkout continue normally.
        trafficState = 'unavailable';
        waiting = [];
        pending = [];
        savePending();
        status();
      });
    return true;
  }
  status();
  let started = false;
  let allowPasswordPage = false;
  let lastPage = '';
  const seen = new Set<string>();
  type Pending = {
    event: string;
    params: Record<string, unknown>;
    id?: string;
    persistent?: boolean;
    at: number;
  };
  let pending: Pending[] = [];
  try {
    const saved = JSON.parse(win.sessionStorage.getItem('cs_analytics_pending_v1') || '[]');
    if (Array.isArray(saved))
      pending = saved
        .filter(
          (p) =>
            typeof p.event === 'string' && p.params && Number.isFinite(p.at) && Date.now() - p.at < 3600000,
        )
        .slice(-100);
  } catch {
    /* Private browsing can disable storage. */
  }
  function savePending() {
    try {
      win.sessionStorage.setItem('cs_analytics_pending_v1', JSON.stringify(pending));
    } catch {
      /* In-memory fallback. */
    }
  }
  if (trafficState === 'excluded') {
    pending = [];
    savePending();
  }
  function defer(event: string, params: Record<string, unknown>, id?: string, persistent = true) {
    if (
      trafficState !== 'enabled' ||
      !enabled ||
      win.location.origin !== origins[surface] ||
      !isSensitiveUrl(win.location.href, allowPasswordPage)
    )
      return;
    if (id && pending.some((p) => p.id === id)) return;
    pending.push({ event, params: safeParams(params), ...(id ? { id, persistent } : {}), at: Date.now() });
    pending = pending.slice(-100);
    savePending();
  }
  function flush() {
    const batch = pending;
    pending = [];
    savePending();
    for (const p of batch) {
      if (Date.now() - p.at >= 3600000) continue;
      if (p.id) once(p.id, p.event, p.params, p.persistent);
      else track(p.event, p.params);
    }
  }
  const allowed = () =>
    trafficState === 'enabled' &&
    enabled &&
    win.location.origin === origins[surface] &&
    !isSensitiveUrl(win.location.href, allowPasswordPage);
  function referrer() {
    try {
      return analyticsLocation(doc.referrer);
    } catch {
      return '';
    }
  }
  function init() {
    if (trafficGate(() => init())) return false;
    if (!allowed()) return false;
    if (started) {
      flush();
      return true;
    }
    started = true;
    restoreFacebookClickCookie(doc, win.location);
    const attribution = readAttributionCookie(doc, win.location)?.values || {};
    const campaign: Record<string, string> = {};
    for (const key of ['source', 'medium', 'campaign', 'content', 'term']) {
      const value = attribution[`utm_${key}`];
      if (value && !/[@?&#=]/.test(value)) campaign[`campaign_${key === 'campaign' ? 'name' : key}`] = value;
    }
    const load = (src: string) => {
      const script = doc.createElement('script');
      script.async = true;
      script.src = src;
      doc.head.appendChild(script);
    };
    win.dataLayer = win.dataLayer || [];
    win.gtag = function () {
      win.dataLayer!.push(arguments);
    };
    win.gtag('js', new Date());
    win.gtag('set', 'linker', { domains: Object.values(origins).map((origin) => new URL(origin).hostname) });
    win.gtag('config', ANALYTICS_IDS.ga, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: analyticsLocation(win.location.href),
      page_referrer: referrer(),
      page_title: `CallSync ${surface}`,
      ...campaign,
      // DebugView; GA4's Developer traffic filter keeps these out of reports.
      ...(testMode ? { debug_mode: true } : {}),
    });
    load(`https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_IDS.ga}`);
    const queue: unknown[] = [];
    win.clarity = Object.assign((...args: unknown[]) => void queue.push(args), { q: queue });
    win.clarity('set', 'surface', surface);
    if (testMode) win.clarity('set', 'test_mode', 'true');
    load(`https://www.clarity.ms/tag/${ANALYTICS_IDS.clarity}`);
    const fbq: NonNullable<Window['fbq']> = Object.assign(
      (...args: unknown[]) => {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue?.push(args);
      },
      { queue: [] as unknown[], loaded: true, version: '2.0' },
    );
    fbq.push = fbq;
    win.fbq = fbq;
    win._fbq = fbq;
    shareFacebookBrowserCookie(doc, win.location);
    fbq('set', 'autoConfig', false, ANALYTICS_IDS.meta);
    fbq('init', ANALYTICS_IDS.meta);
    load('https://connect.facebook.net/en_US/fbevents.js');
    flush();
    return true;
  }
  function page() {
    if (trafficGate(() => page())) return;
    if (!init()) return;
    const location = analyticsLocation(win.location.href);
    if (lastPage === location) return;
    lastPage = location;
    win.gtag?.('event', 'page_view', {
      surface,
      page_location: location,
      page_path: new URL(location).pathname,
      page_title: `CallSync ${surface}`,
      page_referrer: referrer(),
    });
    win.fbq?.('track', 'PageView');
  }
  function track(event: string, params: Record<string, unknown> = {}, eventId?: string) {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(event)) return false;
    if (trafficGate(() => track(event, safeParams(params), eventId))) return false;
    if (!init()) {
      defer(event, params, eventId);
      return false;
    }
    const cleanParams = safeParams(params);
    const product = cleanParams.product_type === 'credits' ? 'callsync_ai_credits' : 'callsync_plan';
    const commerce =
      ['begin_checkout', 'purchase'].includes(event) && typeof cleanParams.value === 'number'
        ? {
            items: [
              {
                item_id: product,
                item_name: product === 'callsync_plan' ? 'CallSync plan' : 'CallSync AI credits',
                price: cleanParams.value,
                quantity: 1,
              },
            ],
          }
        : {};
    const clean = {
      surface,
      ...cleanParams,
      ...commerce,
      page_location: analyticsLocation(win.location.href),
    };
    win.gtag?.('event', event, clean);
    win.clarity?.('event', event);
    for (const key of ['step', 'method', 'product_type', 'payment_provider']) {
      if (typeof cleanParams[key] === 'string') win.clarity?.('set', key, cleanParams[key]);
    }
    if (typeof cleanParams.step === 'string') win.clarity?.('event', `${event}_${cleanParams.step}`);
    const meta = standardMeta[event];
    win.fbq?.(
      meta ? 'track' : 'trackCustom',
      meta || event,
      { surface, ...safeParams(params), ...(testMode ? { test_mode: true } : {}) },
      ...(eventId ? [{ eventID: eventId }] : []),
    );
    return true;
  }
  function once(key: string, event: string, params: Record<string, unknown> = {}, persistent = true) {
    if (trafficGate(() => once(key, event, safeParams(params), persistent))) return;
    if (seen.has(key)) return;
    if (!allowed()) {
      defer(event, params, key, persistent);
      return;
    }
    let ledger: string[] = [];
    try {
      ledger = JSON.parse(win.localStorage.getItem('cs_analytics_once_v1') || '[]');
    } catch {
      /* Storage optional. */
    }
    if (persistent && Array.isArray(ledger) && ledger.includes(key)) return;
    if (!track(event, params, key)) return;
    seen.add(key);
    if (persistent)
      try {
        win.localStorage.setItem(
          'cs_analytics_once_v1',
          JSON.stringify([...(Array.isArray(ledger) ? ledger : []), key].slice(-500)),
        );
      } catch {
        /* Analytics must never block the product. */
      }
  }
  return {
    init: () => {
      try {
        return init();
      } catch {
        return false;
      }
    },
    page: () => {
      try {
        page();
      } catch {
        /* Non-blocking. */
      }
    },
    track: (event: string, params?: Record<string, unknown>) => {
      try {
        track(event, params);
      } catch {
        /* Non-blocking. */
      }
    },
    once: (key: string, event: string, params?: Record<string, unknown>, persistent = true) => {
      try {
        once(key, event, params, persistent);
      } catch {
        /* Non-blocking. */
      }
    },
    passwordLinkReady: () => {
      allowPasswordPage = true;
    },
    clearIdentity: () => {
      pending = [];
      savePending();
      try {
        if (!started) return;
        win.gtag?.('config', ANALYTICS_IDS.ga, { user_id: null, send_page_view: false });
        win.gtag?.('set', 'user_properties', { plan_id: null });
        win.clarity?.('identify', `guest-${win.crypto.randomUUID()}`);
      } catch {
        /* Non-blocking logout. */
      }
    },
    identify: function identifyUser(id: string, planId?: string | null) {
      try {
        if (trafficGate(() => identifyUser(id, planId))) return;
        if (!init()) return;
        win.gtag?.('config', ANALYTICS_IDS.ga, {
          user_id: id,
          send_page_view: false,
          page_location: analyticsLocation(win.location.href),
          page_referrer: referrer(),
        });
        win.gtag?.('set', 'user_properties', { plan_id: planId });
        win.clarity?.('identify', id);
      } catch {
        /* Never send names, emails or phone numbers. */
      }
    },
  };
}
